import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de';
import { CONFIG } from '../config';
import * as helpers from '../../tools/helpers';

const EMPTY_STRING = '';

class BfvDatepicker {
  constructor(options = {}) {
    this.$root = options.element;
    this.datepickerMode = this.$root.getAttribute('data-type') !== '' ? this.$root.getAttribute('data-type') : 'single';
    this.$datepicker = this.$root.getElementsByTagName('input')[0];
    this.dateFrom = this.$root.getAttribute('data-from') !== '' ? this.$root.getAttribute('data-from') : '';

    this.dateTo = this.$root.getAttribute('data-to') !== '' ? this.$root.getAttribute('data-to') : '';
    this.readOnly = this.$root.classList.contains('bfv-datepicker--readonly');
    this.disallowInput = this.$root.classList.contains('bfv-datepicker--disallowInput') || this.$root.getAttribute('data-type') === 'range';
    this.$flatpickr = null;
    this.timerangeValue = this.$datepicker.value;

    if (this.datepickerMode === 'range') {
      this.$fromInput = this.$root.getElementsByClassName('bfv-datepicker--rangeFrom')[0];
      this.$toInput = this.$root.getElementsByClassName('bfv-datepicker--rangeTo')[0];
      this.timerangeTo = this.$toInput.getAttribute('value') !== '' ? this.$toInput.getAttribute('value') : '';
      this.timerangeFrom = this.$fromInput.getAttribute('value') !== '' ? this.$fromInput.getAttribute('value') : '';
    }

    this.defaultDate = this.datepickerMode !== 'single' ? [this.timerangeFrom, this.timerangeTo] : [this.timerangeValue];
  }

  init() {
    try {
      this.$flatpickr = flatpickr(this.$datepicker, {
        mode: this.datepickerMode,
        dateFormat: 'd.m.Y',
        locale: German,
        minDate: this.dateFrom,
        maxDate: this.dateTo,
        clickOpens: !this.readOnly,
        allowInput: !this.disallowInput,
        defaultDate: this.defaultDate,
        onReady: () => {
          const $datepickerMobile = this.$root.querySelector('.flatpickr-mobile');
          if (this.readOnly) {
            if ($datepickerMobile) {
              $datepickerMobile.setAttribute('readonly', true);
            }
          }

          this.setHTMLAttributes();
        },
        onChange: (selectedDates, dateStr, instance) => {
          const $eventTarget = helpers.findParent(this.$datepicker, CONFIG.datepicker.changeListenerClass);
          const requiredSelects = (this.datepickerMode === 'single') ? 1 : 2;

          this.setHTMLAttributes();

          if ($eventTarget && selectedDates.length === requiredSelects) {
            const event = document.createEvent('CustomEvent');

            event.initCustomEvent(CONFIG.events.BFV_DATEPICKER_CHANGED, false, false, {
              selectedDates,
              dateStr: [dateStr],
              instance,
              name: this.$datepicker.getAttribute('name'),
            });

            $eventTarget.dispatchEvent(event);
          }
        },
      });

      this.bindEvents();
    } catch (error) {
      // eslint-disable-next-line
      console.log(error);
    }
  }

  bindEvents() {
    const $eventTarget = helpers.findParent(this.$root, CONFIG.datepicker.clearListenerClass);

    if ($eventTarget) {
      $eventTarget.addEventListener(CONFIG.events.BFV_DATEPICKER_CLEAR, () => {
      });
    }

    if (!this.disallowInput) {
      this.$datepicker.addEventListener('input', (event) => {
        let input = this.$datepicker.value;

        // if delete key was pressed after "." remove it including the last digit
        if (!event.data && /\d{2}$/.test(input) && input.length < 6) {
          input = input.substr(0, input.length - 1);
        }

        // splits the input string by a dot (".") and removes non-digit characters, maps transformed values in "values"
        const values = input.split('.').map(value => value.replace(/\D/g, ''));

        // checks if day and month exceed date limit
        if (values[0]) {
          values[0] = this.checkValue(values[0], 31);
        }
        if (values[1]) {
          values[1] = this.checkValue(values[1], 12);
        }

        // map values to array and add "." if day or month have 2 digits
        const dateArray = values.map((value, index) => (value.length === 2 && index < 2 ? `${value}.` : value));

        this.$datepicker.value = dateArray.join('');
      });

      // if user loses focus on the input field and the input is incomplete/invalid the the input will be empty
      this.$datepicker.addEventListener('blur', (event) => {
        const input = event.target.value;

        const values = input.split('.').map(value => value.replace(/\D/g, ''));
        let dateString = '';

        if (values.length === 3) {
          const day = parseInt(values[0], 10);
          const month = parseInt(values[1], 10) - 1;
          // if year input is incomplete add 2000 to the year, e.g. 23 -> 2023
          const year = values[2].length < 4 ? parseInt(values[2], 10) + 2000 : parseInt(values[2], 10);
          const date = new Date(year, month, day);

          // check if date is valid
          if (!isNaN(date)) {
            // convert date into string
            const dates = [date.getDate(), date.getMonth() + 1, date.getFullYear()];
            dateString = dates.map(value => (value.toString().length === 1 ? `0${value.toString()}` : value.toString())).join('.');
          }
        }
        this.$datepicker.value = dateString;
        this.$flatpickr.setDate(dateString);
      });
    }

    if (this.readOnly) {
      // disable date select opening
      this.$root.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });

      // disable date select opening also on iOS Safari mobile devices
      this.$root.addEventListener('touchend', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    }

    this.$root.addEventListener(CONFIG.events.BFV_RESET_FILTER, () => {
      this.$flatpickr.clear();
    });
  }

  /**
   * this method checks if the date input for days and months exceeds the maximum, e.g. if the user types "4" as the first digit it will be converted to "04" because there aren't any days in a month above 31
   */
  checkValue(str, max) {
    let string = str;
    if (string.charAt(0) !== '0' || string === '00') {
      let num = parseInt(string, 10);
      if (isNaN(num) || num <= 0 || num > max) {
        num = 1;
      }
      string = num > parseInt(max.toString().charAt(0), 10) && num.toString().length === 1 ? `0${num}` : num.toString();
    }
    return string;
  }

  /**
   * this method sets the html value attribute for single and range datepickers and sets the date from and to values for rangedatepickers
   */
  setHTMLAttributes() {
    if (this.datepickerMode === 'range') {
      this.setFromAndToHiddenInputs(this.$datepicker.value);
      this.setFromAndTo();
    }
    this.setValueHTMLAttribute();
  }

  /**
   * this method updates the value of the start- and enddate of a datepicker
   * */
  setFromAndTo() {
    this.timerangeFrom = this.$fromInput.getAttribute('value');
    this.timerangeTo = this.$toInput.getAttribute('value');
  }

  /**
   * this method extracts the start- and enddate out of the compounded daterange value string and sets the from and to date value
   */
  setFromAndToHiddenInputs(inputValue) {
    // date range type append from and to
    const dateParts = inputValue.split(' bis ');
    const fromDate = dateParts[0] ? dateParts[0] : '';
    const toDate = dateParts[1] ? dateParts[1] : '';

    this.$fromInput.setAttribute('value', fromDate);
    this.$toInput.setAttribute('value', toDate);
  }

  /**
   * this method returns the compounded daterange string in case that the startdate and enddate value is set
   * @returns {string}
   */
  getRangeValue() {
    if (this.timerangeFrom && this.timerangeTo) {
      return `${this.timerangeFrom} bis ${this.timerangeTo}`;
    }
    return EMPTY_STRING;
  }

  /**
   * this method returns the value while differenciating between single and range datepickers
   * @returns {string}
   */
  getValue() {
    if (this.datepickerMode === 'range') {
      return this.getRangeValue();
    }
    return this.$datepicker.value;
  }

  /**
   * this method gets the value of the datepicker and sets the html value attribute
   */
  setValueHTMLAttribute() {
    const value = this.getValue();
    this.$datepicker.setAttribute('value', value);
  }
}

export default BfvDatepicker;



// WEBPACK FOOTER //
// ./src/javascript/main/BfvDatepicker/index.js